import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Plane, Save, Loader2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const LowCostCarriersCard = () => {
  const [codes, setCodes] = useState([]);
  const [meta, setMeta] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/settings/low-cost-carriers`, { withCredentials: true });
      setCodes(data.codes || []);
      setMeta(data);
    } catch (e) {
      toast.error([401, 403].includes(e.response?.status) ? 'Admin login required' : 'Failed to load low-cost carriers');
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/admin/settings/low-cost-carriers`, { codes: next }, { withCredentials: true });
      setCodes(data.codes);
      setDraft('');
      toast.success('Low-cost carrier list saved — applies to new searches');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const add = () => {
    const extra = draft.split(/[,\s]+/).map((c) => c.trim().toUpperCase()).filter(Boolean);
    if (!extra.length) return;
    save([...new Set([...codes, ...extra])]);
  };

  return (
    <Card className="border-2 border-orange-200" data-testid="low-cost-carriers-card">
      <CardHeader className="bg-orange-50">
        <CardTitle className="flex items-center justify-between text-orange-700">
          <span className="flex items-center gap-2"><Plane className="h-5 w-5" /> Low-cost Carriers (Duffel)</span>
          {meta && (
            <Badge className={meta.duffel_configured ? (meta.duffel_live_mode ? 'bg-emerald-600' : 'bg-amber-500') : 'bg-slate-400'} data-testid="duffel-mode-badge">
              {meta.duffel_configured ? (meta.duffel_live_mode ? 'Duffel LIVE' : 'Duffel TEST mode') : 'Duffel not configured'}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-5 space-y-3">
        <p className="text-sm text-slate-600">Only these airlines (IATA codes) from Duffel are merged into search results and tagged "Low-cost".</p>
        <div className="flex flex-wrap gap-2" data-testid="low-cost-code-list">
          {codes.map((c) => (
            <span key={c} className="inline-flex items-center gap-1 bg-white border border-orange-200 rounded-full px-3 py-1 text-sm font-mono font-semibold text-slate-700">
              {c}
              <button onClick={() => save(codes.filter((x) => x !== c))} className="text-slate-400 hover:text-red-600" aria-label={`Remove ${c}`} data-testid={`remove-lcc-${c}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="Add codes, e.g. TO, VY" className="w-56 uppercase" data-testid="low-cost-add-input" />
          <Button onClick={add} disabled={saving || !draft.trim()} className="bg-orange-600 hover:bg-orange-700" data-testid="low-cost-add-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Add
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
